import { call, ForkEffect, put, select, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import {
  gemActions,
  IGetGemsPayload,
  IGemCreationPayload,
  IGem,
  IGetGemByIdPayload,
  GemVaultType,
  IGemCreateFormValues,
  IGemUpdateFormValues,
  IGemUpdatePayload,
} from './gemSlice';
import {
  addFavouriteGem,
  getGemById,
  addGem,
  getGems,
  removeFavouriteGem,
  deleteGem,
  editGem,
  getFavGems,
  generateGemPassport,
  generateGemPassportLight,
  toggleGemPublic,
  lockEditGem,
  unlockEditGem,
  checkGemLockStatus,
} from '../../api/gemApi';
import { toastStyles } from '../../utils/styles';
import { uploadImageToS3 } from '../../api/s3Api';
import { RootState } from '../store';
import { ActionStatusEnum } from '../../utils/enums/action-status.enum';
import { userActions } from '../user/userSlice';
import { GemBankType } from '../../utils/enums/gem-bank.enum';

function* watchGetGems({ payload }: { payload: IGetGemsPayload }) {
  try {
    yield put(gemActions.setComponentStatus({ getGemsLoading: true }));
    const response: AxiosResponse = yield call(getGems, payload);
    const gems: IGem[] = response.data.data;
    const favouriteGemIds = gems.filter((gem) => gem.favouriteId).map((gem) => gem.gemId);
    yield put(gemActions.setFavoritesGems(favouriteGemIds));
    yield put(gemActions.setGems(response.data));
    yield put(gemActions.setComponentStatus({ getGemsLoading: false }));
    yield put(userActions.getUserAsync());
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message[0],
        getGemsLoading: false,
      }),
    );
  }
}

function* watchGetFavGems({ payload }: { payload: IGetGemsPayload }) {
  try {
    yield put(gemActions.setComponentStatus({ getGemsLoading: true }));
    const response: AxiosResponse = yield call(getFavGems, payload);
    const favGems: IGem[] = response.data.data;
    const favouriteGemIds = favGems.filter((gem) => gem.favouriteId).map((gem) => gem.gemId);
    yield put(gemActions.setFavoritesGems(favouriteGemIds));
    yield put(gemActions.setFavGems(response.data));
    yield put(gemActions.setComponentStatus({ getGemsLoading: false }));
    yield put(userActions.getUserAsync());
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message[0],
        getGemsLoading: false,
      }),
    );
  }
}

function* watchAddRemoveFavouriteGem({ payload }: { payload: { gemId: string; favouriteId?: string } }) {
  const { gemId, favouriteId } = payload;
  try {
    yield put(gemActions.setComponentStatus({ getGemsLoading: true }));
    yield put(gemActions.setComponentStatus({ getGemLoading: true }));
    yield put(gemActions.setComponentStatus({ addRemoveFavouriteGemLoading: true }));
    if (favouriteId) {
      yield call(removeFavouriteGem, favouriteId);
      toast.success('Gem Removed from Favourites!', toastStyles.error);
    } else {
      const response: AxiosResponse = yield call(addFavouriteGem, gemId);
      toast.success('Gem Added to Favourites!', toastStyles.success);
      const gems: IGem[] = yield select((state: RootState) => state.gem.gems);
      const totalGemsCount: number = yield select((state: RootState) => state.gem.totalGemsCount);
      const updatedGems = gems.map((gem: IGem) => {
        if (gem.gemId === gemId) {
          return { ...gem, favouriteId: response.data.favouriteId };
        }
        return gem;
      });
      yield put(gemActions.resetGetGems());
      yield put(gemActions.setGems({ data: updatedGems, count: totalGemsCount }));
    }
    yield put(gemActions.setFavourites(gemId));
    yield put(gemActions.setComponentStatus({ addRemoveFavouriteGemLoading: false }));
    yield put(gemActions.setComponentStatus({ getGemsLoading: false }));
    yield put(gemActions.setComponentStatus({ getGemLoading: false }));
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message[0],
        addRemoveFavouriteGemLoading: false,
      }),
    );
  }
}

function* watchTogglePublicGem({ payload }: { payload: { gemId: string } }) {
  const { gemId } = payload;
  try {
    yield put(gemActions.setComponentStatus({ getGemsLoading: true }));
    yield put(gemActions.setComponentStatus({ getGemLoading: true }));
    yield put(gemActions.setComponentStatus({ togglePublicGemLoading: true }));

    const response: AxiosResponse = yield call(toggleGemPublic, gemId);
    const gems: IGem[] = yield select((state: RootState) => state.gem.gems);
    const totalGemsCount: number = yield select((state: RootState) => state.gem.totalGemsCount);
    if (response.data.isPublic) {
      toast.success('Gem Added to Porolis GemBANK™!', toastStyles.success);
    } else {
      toast.success('Gem Removed from Porolis GemBANK™!', toastStyles.error);
    }
    const updatedGems = gems.map((gem: IGem) => {
      if (gem.gemId === gemId) {
        return { ...gem, isPublic: response.data.isPublic };
      }
      return gem;
    });
    yield put(gemActions.resetGetGems());
    yield put(gemActions.resetFavourites());
    yield put(gemActions.setGems({ data: updatedGems, count: totalGemsCount }));
    yield put(gemActions.setGem(response.data));
    yield put(gemActions.setComponentStatus({ togglePublicGemLoading: false }));
    yield put(gemActions.setComponentStatus({ getGemsLoading: false }));
    yield put(gemActions.setComponentStatus({ getGemLoading: false }));
    yield put(gemActions.setComponentStatus({ togglePublicGemSuccess: true }));
  } catch (error: any) {
    const gems: IGem[] = yield select((state: RootState) => state.gem.gems);
    const totalGemsCount: number = yield select((state: RootState) => state.gem.totalGemsCount);

    yield put(gemActions.resetGetGems());
    yield put(gemActions.setGems({ data: gems, count: totalGemsCount }));
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message,
        togglePublicGemLoading: false,
        togglePublicGemSuccess: false,
        getGemsLoading: false,
        getGemLoading: false,
      }),
    );
  }
}

function* watchAddGem({ payload }: { payload: { gem: IGemCreateFormValues; image: File; type?: GemVaultType } }) {
  const { gem, image, type } = payload;
  const search: string = yield select((state: RootState) => state.gem.componentStatus.search);
  const filters: [] = yield select((state: RootState) => state.gem.selectedFilterOptions);
  try {
    yield put(gemActions.setComponentStatus({ addGemLoading: true, addEditGemStatus: ActionStatusEnum.LOADING }));
    const fileExtension = image.name.split('.').pop();
    const timestamp = new Date().getTime();

    const imageName = `${timestamp}.${fileExtension}`;
    const renamedImage = new File([image], imageName, { type: image.type });
    const gemData: IGemCreationPayload = {
      species: gem.species,
      weight: parseFloat(gem.weight),
      color: gem.color,
      intensity: gem.intensity,
      secondaryColor: gem.secondaryColor,
      specialDescriptor: gem.specialDescriptor !== '' ? gem.specialDescriptor : undefined,
      depth: parseFloat(gem.depth),
      length: parseFloat(gem.length),
      width: parseFloat(gem.width),
      cut: gem.cut,
      shape: gem.shape,
      clarity: gem.clarity,
      treatment: gem.treatment,
      origin: gem.origin,
      location: gem.location !== '' ? gem.location : undefined,
      certificate: gem.certificate,
      favouriteId: undefined,
      appearanceImage: renamedImage.name,
      appearanceImageContentType: renamedImage.type,
    };
    const response: AxiosResponse = yield call(addGem, gemData);
    const { appearanceImagePresignUrl } = response.data;
    yield call(uploadImageToS3, appearanceImagePresignUrl, renamedImage);
    toast.success('Gem Added!', toastStyles.success);
    yield put(userActions.getUserAsync());
    yield put(gemActions.resetGetGems());

    if (type === GemVaultType.GEM_VAULT) {
      yield put(gemActions.getGemsAsync({ selectedFilterOptions: filters, page: 1, limit: 12, search }));
    }
    if (type === GemVaultType.MY_GEM_FAV) {
      yield put(gemActions.getFavGemsAsync({ selectedFilterOptions: filters, page: 1, limit: 12, search }));
    }
    if (type === GemVaultType.GEM_BANK) {
      yield put(
        gemActions.getGemsAsync({
          selectedFilterOptions: filters,
          page: 1,
          limit: 12,
          type: GemBankType.POROLIS,
          search,
        }),
      );
    }
    yield put(
      gemActions.setComponentStatus({
        addGemLoading: false,
        isAddGemModelOpen: false,
        addEditGemStatus: ActionStatusEnum.SUCCESS,
        search,
      }),
    );
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message[0],
        addRemoveFavouriteGemLoading: false,
        isAddGemModelOpen: false,
        addEditGemStatus: ActionStatusEnum.ERROR,
      }),
    );
    toast.error(error.response.data.message, toastStyles.error);
  }
}

function* watchEditGem({
  payload,
}: {
  payload: { gem: IGemUpdateFormValues; gemId: string; type?: GemVaultType; currentPage: number; currentLimit: number };
}) {
  const { gem, gemId, type, currentPage, currentLimit } = payload;
  const search: string = yield select((state: RootState) => state.gem.componentStatus.search);
  const filters: [] = yield select((state: RootState) => state.gem.selectedFilterOptions);
  try {
    yield put(gemActions.setComponentStatus({ editGemLoading: true, addEditGemStatus: ActionStatusEnum.LOADING }));
    const gemData: IGemUpdatePayload = {
      species: gem.species,
      weight: gem.weight ? parseFloat(gem.weight) : undefined,
      color: gem.color,
      intensity: gem.intensity,
      secondaryColor: gem.secondaryColor !== '' ? gem.secondaryColor : undefined,
      specialDescriptor: gem.specialDescriptor !== '' ? gem.specialDescriptor : undefined,
      depth: gem.depth ? parseFloat(gem.depth) : undefined,
      length: gem.length ? parseFloat(gem.length) : undefined,
      width: gem.width ? parseFloat(gem.width) : undefined,
      cut: gem.cut,
      shape: gem.shape,
      clarity: gem.clarity !== '' ? gem.clarity : undefined,
      treatment: gem.treatment !== '' ? gem.treatment : undefined,
      origin: gem.origin !== '' ? gem.origin : undefined,
      location: gem.location !== '' ? gem.location : undefined,
      certificate: gem.certificate !== '' ? gem.certificate : undefined,
      isPublic: gem.isPublic,
    };
    yield call(editGem, { gem: gemData, gemId });
    toast.success('Gem Updated!', toastStyles.success);
    yield put(gemActions.resetGetGems());

    if (type === GemVaultType.GEM_VAULT) {
      yield put(
        gemActions.getGemsAsync({ selectedFilterOptions: filters, page: currentPage, limit: currentLimit, search }),
      );
    }
    if (type === GemVaultType.MY_GEM_FAV) {
      yield put(
        gemActions.getFavGemsAsync({ selectedFilterOptions: filters, page: currentPage, limit: currentLimit, search }),
      );
    }
    if (type === GemVaultType.GEM_BANK) {
      yield put(
        gemActions.getGemsAsync({
          selectedFilterOptions: filters,
          page: currentPage,
          limit: currentLimit,
          type: GemBankType.POROLIS,
          search,
        }),
      );
    }

    yield put(gemActions.setSelectedFilterOptions(filters));

    yield put(
      gemActions.setComponentStatus({
        editGemLoading: false,
        isAddGemModelOpen: false,
        addEditGemStatus: ActionStatusEnum.SUCCESS,
        search,
      }),
    );
    yield call(unlockEditGem, gemId);
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        isAddGemModelOpen: false,
        error: error.response.data.message,
        addEditGemStatus: ActionStatusEnum.ERROR,
        editGemLoading: false,
      }),
    );
    toast.error('Error Updating Gem!', toastStyles.error);
  }
}
function* watchDeleteGem({ payload }: { payload: { gemId: string; type?: GemVaultType } }) {
  const { gemId, type } = payload;
  const search: string = yield select((state: RootState) => state.gem.componentStatus.search);
  const filters: [] = yield select((state: RootState) => state.gem.selectedFilterOptions);
  try {
    yield put(gemActions.setComponentStatus({ deleteGemLoading: true }));

    yield call(deleteGem, gemId);
    toast.success('Gem Deleted!', toastStyles.error);
    yield put(userActions.getUserAsync());
    yield put(gemActions.resetGetGems());
    if (type === GemVaultType.GEM_VAULT) {
      yield put(gemActions.getGemsAsync({ selectedFilterOptions: filters, page: 1, limit: 12, search }));
    }
    if (type === GemVaultType.MY_GEM_FAV) {
      yield put(gemActions.getFavGemsAsync({ selectedFilterOptions: filters, page: 1, limit: 12, search }));
    }
    if (type === GemVaultType.GEM_BANK) {
      yield put(
        gemActions.getGemsAsync({
          selectedFilterOptions: filters,
          page: 1,
          limit: 12,
          type: GemBankType.POROLIS,
          search,
        }),
      );
    }

    yield put(gemActions.setSelectedFilterOptions(filters));

    yield put(
      gemActions.setComponentStatus({
        deleteGemLoading: false,
        isAddGemModelOpen: false,
        isDeleteGemModelOpen: false,
        search,
      }),
    );
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message,
        deleteGemLoading: false,
        isAddGemModelOpen: false,
        isDeleteGemModelOpen: false,
      }),
    );

    toast.error('Error Deleting Gem!', toastStyles.error);
  }
}
function* watchGetGemById({ payload }: { payload: IGetGemByIdPayload }) {
  try {
    yield put(gemActions.setComponentStatus({ getGemLoading: true }));
    const response: AxiosResponse = yield call(getGemById, payload);
    yield put(gemActions.setGem(response.data));
    yield put(gemActions.setComponentStatus({ getGemLoading: false }));
    yield put(userActions.getUserAsync());
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message,
        getGemLoading: false,
      }),
    );
  }
}

function* watchOpenEditGemModel({ payload }: { payload: { gem: IGem } }) {
  if (payload.gem.is_scanned) {
    yield call(lockEditGem, payload.gem.gemId);
  }
  yield put(gemActions.setComponentStatus({ isAddGemModelOpen: true }));
  yield put(gemActions.setGemToEdit(payload.gem));
}

function* watchCloseEditGemModel({ payload }: { payload: { gemId: string | null } }) {
  yield put(gemActions.setComponentStatus({ isAddGemModelOpen: false }));
  if (payload.gemId) {
    yield call(unlockEditGem, payload.gemId);
  }
}

function* watchOpenEditGemConfirmationModel({ payload }: { payload: { gem: IGem } }) {
  yield put(gemActions.setComponentStatus({ isEditGemConfirmationModelOpen: true }));
  yield put(gemActions.setGemToOpen(payload.gem));
}

function* watchCheckGemLockStatus({ payload }: { payload: { gemId: string } }) {
  try {
    yield put(gemActions.setComponentStatus({ isCheckGemLockStatusLoading: true }));

    const response: AxiosResponse = yield call(checkGemLockStatus, payload.gemId);
    if (response.data.isLocked) {
      yield put(gemActions.setLockedGem({ gemId: payload.gemId }));
    }
  } catch (error) {
    console.error('Error checking gem lock status:', error);
  } finally {
    yield put(gemActions.setComponentStatus({ isCheckGemLockStatusLoading: false }));
  }
}

function* watchGenerateGemPassport({ payload }: { payload: { gemId: string } }) {
  try {
    yield put(gemActions.setComponentStatus({ generateGemPassportLoading: true }));
    yield call(generateGemPassport, payload.gemId);
    yield put(gemActions.setComponentStatus({ generateGemPassportLoading: false }));
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message,
        generateGemPassportLoading: false,
      }),
    );
  }
}

function* watchGenerateGemPassportLight({ payload }: { payload: { gemId: string } }) {
  try {
    yield put(gemActions.setComponentStatus({ generateGemPassportLiteLoading: true }));
    const response: AxiosResponse = yield call(generateGemPassportLight, payload.gemId);
    if (response.status === 200) {
      yield put(gemActions.getGemByIdAsync({ gemId: payload.gemId }));

      yield put(gemActions.setGemPassportLiteFirstTimeGenerated(true));
    }
    yield put(gemActions.setComponentStatus({ generateGemPassportLiteLoading: false }));
  } catch (error: any) {
    yield put(
      gemActions.setComponentStatus({
        error: error.response.data.message,
        generateGemPassportLiteLoading: false,
      }),
    );
  }
}

export function* watchGemSagas(): Generator<ForkEffect, void> {
  yield takeLatest(gemActions.getGemsAsync, watchGetGems);
  yield takeLatest(gemActions.getFavGemsAsync, watchGetFavGems);
  yield takeLatest(gemActions.addRemoveFavouriteGemAsync, watchAddRemoveFavouriteGem);
  yield takeLatest(gemActions.togglePublicGemAsync, watchTogglePublicGem);
  yield takeLatest(gemActions.getGemByIdAsync, watchGetGemById);
  yield takeLatest(gemActions.addGemAsync, watchAddGem);
  yield takeLatest(gemActions.editGemAsync, watchEditGem);
  yield takeLatest(gemActions.deleteGemAsync, watchDeleteGem);
  yield takeLatest(gemActions.openEditGemModel, watchOpenEditGemModel);
  yield takeLatest(gemActions.closeGemModel, watchCloseEditGemModel);
  yield takeLatest(gemActions.openEditGemConfirmationModel, watchOpenEditGemConfirmationModel);
  yield takeLatest(gemActions.generateGemPassportAsync, watchGenerateGemPassport);
  yield takeLatest(gemActions.generateGemPassportLightAsync, watchGenerateGemPassportLight);
  yield takeLatest(gemActions.checkGemLockStatus, watchCheckGemLockStatus);
}

const gemSagas = watchGemSagas;
export default gemSagas;
